/**
 * What a board item is drawn as and how it looks, pure: shared by the board (`windows/graph/board`)
 * and the prototype (`state/prototype`), so a screen looks the same on the board, in the
 * Prototype window and in the exported site.
 *
 * An item is drawn by the word its kind id holds (`art_note` and `ui_note` are notes, `ui_button`
 * a button...); its look is its `style`, values by key: `fill`, `stroke`, `color`, `size`,
 * `weight`, `italic`, `underline`, `strike`, `align`, `font`, `radius`, `fit` (a picture: `cover` or `contain`), and for
 * UI elements `secret` (an input hiding what is typed) and `checked` (a box ticked at first).
 * Values are checked before they become CSS: a colour is a colour, a size a number, so nothing
 * typed can reach another property or fetch anything.
 */

/** What an item is drawn as. */
export type ItemShape = "note" | "text" | "shape" | "image" | "drawing" | "button" | "input" | "checkbox" | "list" | "instance" | "card";

/** What an item of `kind` is drawn as, from the word its id holds. */
export function itemShape(kind: string): ItemShape {
  if (/note/.test(kind)) return "note";
  if (/button/.test(kind)) return "button";
  if (/input/.test(kind)) return "input";
  if (/checkbox/.test(kind)) return "checkbox";
  if (/list/.test(kind)) return "list";
  if (/instance/.test(kind)) return "instance";
  if (/text/.test(kind)) return "text";
  if (/shape|rect/.test(kind)) return "shape";
  if (/image/.test(kind)) return "image";
  if (/drawing/.test(kind)) return "drawing";
  return "card";
}

/** Whether an item drawn as `shape` shows its words (a UI rectangle's name is only its layer's). */
export function showsWords(shape: ItemShape, kind: string): boolean {
  return shape !== "image" && shape !== "drawing" && shape !== "instance" && !(shape === "shape" && /^ui_/.test(kind));
}

/** A screen's device, by the word `style.device` holds: its size in CSS pixels. */
export const DEVICES: Record<string, { label: string; w: number; h: number }> = {
  phone: { label: "Phone", w: 390, h: 844 },
  tablet: { label: "Tablet", w: 820, h: 1180 },
  desktop: { label: "Desktop", w: 1440, h: 1024 },
  watch: { label: "Watch", w: 198, h: 242 },
};

/**
 * The fonts offered, by the word `style.font` holds: the families (those from Google Fonts are
 * loaded by `index.html`; without the network, the generic family after them).
 */
export const FONTS: Record<string, string> = {
  sans: "var(--font-ui, sans-serif)",
  rounded: "'Nunito', 'Arial Rounded MT Bold', sans-serif",
  display: "'Oswald', 'Arial Narrow', sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  elegant: "'Playfair Display', Georgia, serif",
  slab: "'Roboto Slab', Rockwell, serif",
  mono: "ui-monospace, 'Cascadia Code', monospace",
  typewriter: "'Courier Prime', 'Courier New', monospace",
  hand: "'Caveat', 'Comic Neue', 'Comic Sans MS', cursive",
  marker: "'Permanent Marker', 'Comic Sans MS', cursive",
};

/** The name each font of {@link FONTS} is offered under. */
export const FONT_NAMES: Record<string, string> = {
  sans: "Instrument Sans",
  rounded: "Nunito",
  display: "Oswald",
  serif: "Georgia",
  elegant: "Playfair Display",
  slab: "Roboto Slab",
  mono: "Monospace",
  typewriter: "Courier Prime",
  hand: "Caveat (handwriting)",
  marker: "Permanent Marker",
};

/** The smallest and biggest words an item may have (pixels). */
export const TEXT_SIZES = { min: 1, max: 400 } as const;

/** The accent a new button takes. */
export const ACCENT = "#6a4be0";

/** CSS declarations by property name. */
export type Css = Record<string, string>;

/** An item's style values by key. */
export type Style = { readonly [key in string]?: string };

/** `value` when it is a colour (`#rgb`…`#rrggbbaa`, a colour's name, `rgb()`/`rgba()` of numbers), else `undefined`. */
export function safeColour(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const v = value.trim();
  return /^#[0-9a-f]{3,8}$/i.test(v) || /^[a-z]{3,20}$/i.test(v) || /^rgba?\(\s*[\d.]+%?\s*,\s*[\d.]+%?\s*,\s*[\d.]+%?\s*(,\s*[\d.]+%?\s*)?\)$/i.test(v) ? v : undefined;
}

/** `value` as a number of pixels within `[min, max]`, else `fallback`. */
export function pixels(value: string | undefined, fallback: number, min = 0, max = 400): number {
  const n = Number(value);
  return value !== undefined && value !== "" && Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
}

/** The look of an item drawn as `shape` from its style, as CSS declarations. */
export function lookOf(shape: ItemShape, style: Style): Css {
  const css: Css = {};
  const set = (key: string, value: string | undefined) => value !== undefined && (css[key] = value);
  const align = style.align === "left" || style.align === "center" || style.align === "right" ? style.align : undefined;
  set("font-size", `${pixels(style.size, shape === "text" ? 18 : shape === "button" || shape === "input" || shape === "checkbox" || shape === "list" ? 15 : 16, TEXT_SIZES.min, TEXT_SIZES.max)}px`);
  set("font-weight", style.weight === "bold" ? "700" : undefined);
  set("font-style", style.italic === "yes" ? "italic" : undefined);
  const lines = [style.underline === "yes" ? "underline" : "", style.strike === "yes" ? "line-through" : ""].filter(Boolean).join(" ");
  set("text-decoration", lines || undefined);
  set("text-align", align ?? (shape === "text" || shape === "list" || shape === "checkbox" || shape === "input" ? "left" : "center"));
  set("font-family", style.font ? FONTS[style.font] : undefined);
  set("color", safeColour(style.color));
  set("border-radius", style.radius !== undefined ? `${pixels(style.radius, 0)}px` : undefined);
  const fill = safeColour(style.fill);
  const stroke = safeColour(style.stroke);
  switch (shape) {
    case "note":
      css.background = fill ?? "#ffe680";
      break;
    case "shape":
    case "card":
      css.background = fill ?? (shape === "card" ? "var(--panel, #ffffff)" : "#cfe3ff");
      set("border-color", stroke);
      break;
    case "text":
    case "list":
    case "checkbox":
      set("background", fill);
      break;
    case "button":
      css.background = fill ?? ACCENT;
      css.color ??= "#ffffff";
      css["border-radius"] ??= "8px";
      css.border = stroke ? `1.5px solid ${stroke}` : "none";
      css["justify-content"] = align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center";
      break;
    case "input":
      css.background = fill ?? "#ffffff";
      css.border = `1px solid ${stroke ?? "#c9c6d6"}`;
      css["border-radius"] ??= "6px";
      css.color ??= "#1f1d2b";
      break;
    case "image":
      css["object-fit"] = style.fit === "contain" ? "contain" : "cover";
      break;
    case "instance":
    case "drawing":
      break;
  }
  return css;
}

/** A frame's look (a screen, a component, a box inside a screen): its fill, outline and corners. */
export function frameLook(style: Style): Css {
  const css: Css = {};
  const fill = safeColour(style.fill);
  const stroke = safeColour(style.stroke);
  if (fill) css.background = fill;
  if (stroke) css["border-color"] = stroke;
  if (style.radius !== undefined) css["border-radius"] = `${pixels(style.radius, 0)}px`;
  return css;
}

/** Declarations as the text of a `style` attribute. */
export function cssText(css: Css): string {
  return Object.entries(css)
    .map(([k, v]) => `${k}:${v}`)
    .join(";");
}
